/**
 * Admin customer overview API client (PBI #1126 marketing opt-in).
 */

import apiClient from './apiClient';

/** Full customer detail from GET /admin/customers/{id}. */
export interface AdminCustomerDetail {
  id: string;
  name: string | null;
  email: string | null;
  country: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  subscriptionPlanName: string | null;
  subscriptionStatus: string | null;
  subscribedAt: string | null;
  billingInterval: string | null;
  contactsCount: number;
  scheduledItemsCount: number;
  hasCompletedOnboarding: boolean;
  termsAcceptedAt: string | null;
  termsAcceptedVersion: string | null;
  aiRegenerationCount: number;
  aiRegenerationCountResetDate: string | null;
  freeContactLimitOverride: number | null;
  /** Marketing / nurture opt-in (same flag as My Account). */
  marketingEmailsEnabled: boolean;
  /** Whether an admin has disabled sign-in for this customer (PBI #1230). */
  isAccountDisabled: boolean;
  /** Super-admin-flagged demo account: simulated orders, suppressed email (PBI #1347). */
  isDemoAccount: boolean;
  /** UTC when the account was disabled; null when active. */
  accountDisabledAtUtc: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  cancelledAt: string | null;
  unpaidFlowerOrderCount: number;
  unpaidFlowerOrders: Array<{
    id: number;
    productName: string;
    updatedAt: string;
  }>;
  cameViaPaidAd: boolean;
  /** Derived network label (Google Ads, OpenAI Ads, …); null when organic. */
  acquisitionProvider: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  msclkid: string | null;
  fbclid: string | null;
  /** ChatGPT Ads oppref click id. */
  oppref: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  landingPath: string | null;
  attributedAtUtc: string | null;
  /** Analytics cookie opt-in; null = Unknown (never captured). PBI #1154 */
  cookieAnalyticsConsent: boolean | null;
  /** Marketing cookie opt-in; null = Unknown (never captured). PBI #1154 */
  cookieMarketingConsent: boolean | null;
  /** UTC when cookie consent was first written. */
  cookieConsentCapturedAtUtc: string | null;
  /** UTC when cookie consent was last updated. */
  cookieConsentUpdatedAtUtc: string | null;
}

/** Response from PATCH /admin/customers/{id}/marketing-emails. */
export interface AdminCustomerMarketingEmails {
  userId: string;
  marketingEmailsEnabled: boolean;
}

/** Response from PATCH /admin/customers/{id}/account-status (PBI #1230). */
export interface AdminCustomerAccountStatus {
  userId: string;
  isAccountDisabled: boolean;
  accountDisabledAtUtc: string | null;
}

/**
 * Loads extended customer detail for the admin modal.
 */
export async function getAdminCustomerDetail(userId: string): Promise<AdminCustomerDetail> {
  const response = await apiClient.get<AdminCustomerDetail>(
    `/admin/customers/${encodeURIComponent(userId)}`,
  );
  return response.data;
}

/**
 * Sets marketing / nurture email opt-in for a customer (support/admin).
 */
export async function updateAdminCustomerMarketingEmails(
  userId: string,
  marketingEmailsEnabled: boolean,
): Promise<AdminCustomerMarketingEmails> {
  const response = await apiClient.patch<AdminCustomerMarketingEmails>(
    `/admin/customers/${encodeURIComponent(userId)}/marketing-emails`,
    { marketingEmailsEnabled },
  );
  return response.data;
}

/**
 * Disables or re-enables a customer account (PBI #1230).
 * On disable, cancels Stripe at period end when applicable.
 */
export async function updateAdminCustomerAccountStatus(
  userId: string,
  isAccountDisabled: boolean,
): Promise<AdminCustomerAccountStatus> {
  const response = await apiClient.patch<AdminCustomerAccountStatus>(
    `/admin/customers/${encodeURIComponent(userId)}/account-status`,
    { isAccountDisabled },
  );
  return response.data;
}
